import { useEnterLecturePageMode, useExitLecturePageMode, useLecturePageLink, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import React, { useContext, useCallback, useMemo } from 'react';

import { AngularServicesContext } from 'react-app';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { unsetCurrentCourseCourseLongTeamSet, unsetCurrentCourseGroupTeamSet } from 'redux/actions/lecture-components';
import { duplicateLecturePage, deleteLecturePage, createLecturePage } from 'redux/actions/lecture-pages';
import { getCourseTimeline } from 'redux/actions/timeline';
import { RootState } from 'redux/schemas';
import { CourseAliases } from 'redux/schemas/models/course';
import { LecturePage } from 'redux/schemas/models/lecture-page';
import { getCourseAliases, getCurrentCourseCourseLongTeamSet, getCurrentCourseGroupTeamSet, getFlatCourseAliases } from 'redux/selectors/course';
import { CannotBeCopiedComponentsMeta, getCannotBeCopiedComponentsMeta, getNearestLecture } from 'redux/selectors/lecture-page';
import { useAppDispatch } from 'redux/store';
import NvDropdown, { NvDropdownOption, NvDropdownButtonStyle, NvDropdownAlign } from 'shared/components/inputs/nv-dropdown';
import { isEmpty } from 'underscore';
import { LecturePageMode } from '..';

export const LectureContentAdminDropdown = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();
  const enterMode = useEnterLecturePageMode();
  const exitMode = useExitLecturePageMode();

  const { $state, CurrentPermissionsManager, CurrentCourseManager } = useContext(AngularServicesContext);

  const lectureName = useSelector(state => state.models.lecturePages[state.app.lecturePage.currentLectureId].title);
  const currentUserId = useSelector(state => state.app.currentUserId);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const cannotBeCopiedComponentsMeta: CannotBeCopiedComponentsMeta[] = useSelector((state: RootState) => getCannotBeCopiedComponentsMeta(state, state.app.lecturePage.currentLectureId));
  const aliases: CourseAliases = useSelector((state: RootState) => getCourseAliases(state));
  const currentCourseLongTeamSet = useSelector(state => getCurrentCourseCourseLongTeamSet(state));
  const currentCourseGroupTeamSet = useSelector(state => getCurrentCourseGroupTeamSet(state));

  const nearestLecturePageId = useSelector(state => getNearestLecture(state, state.app.lecturePage.currentLectureId));

  const duplicateThisLecture = useCallback(() => {
    // TODO: This request can take a while. On prod, we do not show any kind of loading indicator. But it feels
    // like a bad UX; let's try adding a loading indicator here and getting PM's feedback
    dispatch(duplicateLecturePage({ catalogId: params.catalogId, lecturePageId: params.lecturePageId })).then(result => {
      lectureLink({
        ...params,
        mode: LecturePageMode.EDIT,
        lecturePageId: (result.payload as LecturePage).id,
      });

      if (cannotBeCopiedComponentsMeta?.length > 0) {
        const bodyText: JSX.Element = (
          <div>
            {t.LECTURE_PAGES.ADMIN.DUPLICATED.DESCRIPTION(aliases.lectureAliases)}
            <div className='text-left text-medium font-weight-bold ml-6 pl-6 mt-4'>
              <ul className='ml-4 pl-4 mb-0'>
                {cannotBeCopiedComponentsMeta.map((lcMeta, idx) => (
                  <li key={lcMeta.lectureComponentId}>{lcMeta.label}</li>
                ))}
              </ul>
            </div>
          </div>
        );

        dispatch(openConfirmationDialog({
          title: t.LECTURE_PAGES.ADMIN.DUPLICATED.TITLE(courseAliases),
          bodyText,
          confirmButtonVariant: 'primary',
          confirmText: t.FORM.CLOSE(),
          onConfirm: () => {},
          noCancelButton: true,
          icon: 'info',
        }));
      }
    });
  }, [courseAliases, dispatch, lectureLink, params, aliases.lectureAliases, cannotBeCopiedComponentsMeta]);

  const deleteThisLecture = useCallback(() => {
    dispatch(openConfirmationDialog({
      // TODO: We need to add the title lesson here.
      title: t.TIMELINE.DELETE_LESSON.ARE_YOU_SURE_LECTURE_NAME({ ...courseAliases, lectureName }),
      bodyText: t.TIMELINE.DELETE_LESSON.DELETE_ALL_SUBMISSIONS({ ...courseAliases, lessonName: lectureName }),
      confirmText: t.FORM.YES_SURE(),
      onConfirm: () => {
        if (nearestLecturePageId) {
          lectureLink({
            ...params,
            lecturePageId: nearestLecturePageId,
          });
        }

        dispatch(deleteLecturePage({ catalogId: params.catalogId, lecturePageId: params.lecturePageId })).then(result => {
          /**
           * Clearing the courseLongTeamSet/groupTeamSet if the deleted lecture
           * page has the Team/Group Formation LC.
           */
          if (
            !isEmpty(currentCourseLongTeamSet)
            && currentCourseLongTeamSet.lecturePageId === params.lecturePageId
          ) {
            CurrentCourseManager.course.courseLongTeamSet = null;
            dispatch(unsetCurrentCourseCourseLongTeamSet());
          }

          if (
            !isEmpty(currentCourseGroupTeamSet)
            && currentCourseGroupTeamSet.lecturePageId === params.lecturePageId
          ) {
            CurrentCourseManager.course.groupTeamSet = null;
            dispatch(unsetCurrentCourseGroupTeamSet());
          }

          /**
           * Go to course home, if no near lessons found. It is redirected to
           * course home only after the delete to display the data as updated.
           */
          if (!nearestLecturePageId) {
            $state.go('course-home', { catalogId: params.catalogId });
          }
        });
      },
    }));
  }, [
    $state, CurrentCourseManager.course, courseAliases, nearestLecturePageId,
    currentCourseGroupTeamSet, currentCourseLongTeamSet, dispatch, lectureLink,
    lectureName, params,
  ]);

  const createLesson = useCallback(() => {
    dispatch(createLecturePage({ catalogId: params.catalogId, previousLectureId: params.lecturePageId })).then(actionResult => {
      lectureLink({
        ...params,
        mode: LecturePageMode.EDIT,
        lecturePageId: (actionResult.payload as LecturePage).id,
      });

      // Re-fetch the course timeline with the new lecture page
      dispatch(getCourseTimeline({
        catalogId: params.catalogId,
        userId: currentUserId,
        outlineOnly: false,
      }));
    });
  }, [currentUserId, dispatch, lectureLink, params]);

  const adminDropdownItems = useMemo<NvDropdownOption[]>(() => {
    const items: NvDropdownOption[] = [];

    if (params.mode === LecturePageMode.EDIT) {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EXIT_EDIT_MODE(),
        callback: () => exitMode(),
        dataQa: 'exit-edit-mode-option',
      });
    } else if (params.mode === LecturePageMode.RESTRICTED_EDIT) {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EXIT_ADMIN_MODE(),
        callback: () => exitMode(),
        dataQa: 'exit-admin-mode-option',
      });
    } else {
      items.push({
        type: 'text',
        text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.EDIT(),
        callback: () => enterMode(LecturePageMode.EDIT),
        dataQa: 'edit-lesson-option',
      });
    }

    if (CurrentPermissionsManager.isCourseBuilder()) {
      items.push(
        /** Preserve the previous mode entered in the nav logic if we entered from an edit mode. This way we will
         * return to the edit mode when exiting the reorder mode */
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.REORDER(),
          callback: () => enterMode(
            LecturePageMode.REORDER,
            params.mode === LecturePageMode.EDIT || params.mode === LecturePageMode.RESTRICTED_EDIT,
          ),
          dataQa: 'reorder-lesson-option',
        },
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.DUPLICATE(courseAliases),
          callback: duplicateThisLecture,
          dataQa: 'duplicate-lesson-option',
        },
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.DELETE(),
          class: 'text-danger',
          callback: deleteThisLecture,
          dataQa: 'delete-lesson-option',
        },
        { type: 'divider' },
        {
          type: 'text',
          text: t.LECTURE_PAGES.ADMIN.COG_OPTIONS.CREATE(courseAliases),
          callback: createLesson,
          dataQa: 'create-lesson-option',
        },
      );
    }

    return items;
  }, [params.mode, CurrentPermissionsManager, exitMode, enterMode, courseAliases, duplicateThisLecture, deleteThisLecture, createLesson]);

  return (
    <NvDropdown
      buttonStyle={NvDropdownButtonStyle.ICON}
      iconClass='icon-smallest icon-settings pr-4'
      items={adminDropdownItems}
      align={NvDropdownAlign.RIGHT}
      offset={10}
      minWidth={220}
      onToggle={(showList) => {
        if (showList && params.mode === LecturePageMode.EDIT) {
          hideAddUI(dispatch);
        }
      }}
      toggleDataQa='lecture-page-content-settings'
    />
  );
};

export default LectureContentAdminDropdown;
